import React, { useState, useEffect, useCallback } from 'react';
import Shell from '../dashboard/Shell';
import { useToast } from '../../components/ui/Toast';
import axiosInstance from '../../axiosConfig';
import VisualSegmentBuilder from './VisualSegmentBuilder';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureGate from '../../components/FeatureGate';
import './TagsSegmentsManagement.css';

const TAG_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

const TagsSegmentsManagement = () => {
  const toast = useToast();
  const { hasAccess, loading: featureLoading } = useFeatureAccess('marketing.tags');
  const [activeTab, setActiveTab] = useState('tags');
  const [loading, setLoading] = useState(true);

  // Tags state
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagCustomers, setTagCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  // Tag list filtering / sorting — keeps the management page usable as the
  // tag count grows.
  const [tagSearch, setTagSearch] = useState('');
  const [tagSort, setTagSort] = useState('alpha'); // alpha | mostUsed | newest

  // Customer selection for bulk tagging
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loadingAllCustomers, setLoadingAllCustomers] = useState(false);

  // Segments state
  const [segments, setSegments] = useState([]);
  const [showSegmentBuilder, setShowSegmentBuilder] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);

  // Automations state
  const [automations, setAutomations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [automationForm, setAutomationForm] = useState({
    name: '',
    trigger: 'tag_added',
    tagName: '',
    action: 'send_campaign',
    campaignId: '',
    templateId: '',
    isActive: true
  });

  // Auto-tagging rules state
  const [autoTaggingRules, setAutoTaggingRules] = useState([]);
  const [showAutoTagModal, setShowAutoTagModal] = useState(false);
  const [editingAutoTagRule, setEditingAutoTagRule] = useState(null);
  const [autoTagForm, setAutoTagForm] = useState({
    name: '',
    event: 'first_purchase',
    condition: '',
    threshold: '',
    tags: [],
    isActive: true
  });

  // Fetch tags
  const fetchTags = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/merchant/tags');
      if (response.data.success) {
        setTags(response.data.tags || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load tags');
    }
  }, [toast]);

  // Fetch segments
  const fetchSegments = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/merchant/segments');
      if (response.data.success) {
        setSegments(response.data.segments || []);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
      toast.error('Failed to load segments');
    }
  }, [toast]);

  // Fetch automations
  const fetchAutomations = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/merchant/automations');
      if (response.data.success) {
        setAutomations(response.data.automations || []);
      }
    } catch (error) {
      console.error('Error fetching automations:', error);
    }
  }, []);

  // Fetch campaigns for dropdown
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/merchant/automations/campaigns');
      if (response.data.success) {
        setCampaigns(response.data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, []);

  // Fetch templates for dropdown
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/merchants/email-templates');
      if (response.data.success) {
        setTemplates(response.data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  // Fetch auto-tagging rules
  const fetchAutoTaggingRules = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/merchant/auto-tagging-rules');
      if (response.data.success) {
        setAutoTaggingRules(response.data.rules || []);
      }
    } catch (error) {
      console.error('Error fetching auto-tagging rules:', error);
    }
  }, []);

  useEffect(() => {
    // Only load data if user has feature access
    if (!hasAccess || featureLoading) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTags(), fetchSegments(), fetchAutomations(), fetchCampaigns(), fetchTemplates(), fetchAutoTaggingRules()]);
      setLoading(false);
    };
    loadData();
  }, [hasAccess, featureLoading, fetchTags, fetchSegments, fetchAutomations, fetchCampaigns, fetchTemplates, fetchAutoTaggingRules]);

  // Create tag
  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;

    try {
      const response = await axiosInstance.post('/merchant/tags', {
        name: newTagName,
        color: newTagColor
      });
      if (response.data.success) {
        toast.success(`Tag "${newTagName}" created`);
        setNewTagName('');
        setNewTagColor(TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)]);
        fetchTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create tag');
    }
  };

  // Delete tag
  const handleDeleteTag = async (tagName) => {
    if (!window.confirm(`Delete tag "${tagName}"? It will be removed from all customers.`)) return;

    try {
      const response = await axiosInstance.delete(`/merchant/tags/${encodeURIComponent(tagName)}`);
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedTag(null);
        fetchTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete tag');
    }
  };

  // View customers with tag
  const handleViewTagCustomers = async (tag) => {
    setSelectedTag(tag);
    setLoadingCustomers(true);
    try {
      const response = await axiosInstance.get(`/merchant/tags/${encodeURIComponent(tag.name)}/customers`);
      if (response.data.success) {
        setTagCustomers(response.data.customers || []);
      }
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Fetch all customers for bulk tagging
  const fetchAllCustomers = async (search = '') => {
    setLoadingAllCustomers(true);
    try {
      const response = await axiosInstance.get(`/merchant/all-customers${search ? `?search=${search}` : ''}`);
      if (response.data.success) {
        setAllCustomers(response.data.customers || []);
      }
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoadingAllCustomers(false);
    }
  };

  // Open customer selector
  const openCustomerSelector = () => {
    setShowCustomerSelector(true);
    setSelectedCustomers([]);
    fetchAllCustomers();
  };

  // Add selected customers to tag
  const handleAddCustomersToTag = async () => {
    if (!selectedTag || selectedCustomers.length === 0) return;

    try {
      const response = await axiosInstance.post('/merchant/tags/bulk-add', {
        customerIds: selectedCustomers,
        tags: [selectedTag.name]
      });
      if (response.data.success) {
        toast.success(`Added ${response.data.updated} customers to "${selectedTag.name}"`);
        setShowCustomerSelector(false);
        setSelectedCustomers([]);
        handleViewTagCustomers(selectedTag);
        fetchTags();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add customers');
    }
  };

  // Remove customer from tag
  const handleRemoveCustomerFromTag = async (customerId) => {
    if (!selectedTag) return;

    try {
      const response = await axiosInstance.post('/merchant/tags/bulk-remove', {
        customerIds: [customerId],
        tags: [selectedTag.name]
      });
      if (response.data.success) {
        toast.success('Customer removed from tag');
        handleViewTagCustomers(selectedTag);
        fetchTags();
      }
    } catch (error) {
      toast.error('Failed to remove customer');
    }
  };

  // Toggle customer selection
  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  // Select all visible customers
  const selectAllCustomers = () => {
    const visibleIds = allCustomers
      .filter(c => !tagCustomers.some(tc => tc.id === c.id))
      .map(c => c.id);
    setSelectedCustomers(visibleIds);
  };

  // Delete segment
  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm('Delete this segment?')) return;

    try {
      const response = await axiosInstance.delete(`/merchant/segments/${segmentId}`);
      if (response.data.success) {
        toast.success('Segment deleted');
        fetchSegments();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete segment');
    }
  };

  // Save segment
  const handleSaveSegment = async (segmentData) => {
    try {
      if (editingSegment) {
        const response = await axiosInstance.put(`/merchant/segments/${editingSegment.id}`, segmentData);
        if (response.data.success) {
          toast.success('Segment updated');
        }
      } else {
        const response = await axiosInstance.post('/merchant/segments', segmentData);
        if (response.data.success) {
          toast.success('Segment created');
        }
      }
      setShowSegmentBuilder(false);
      setEditingSegment(null);
      fetchSegments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save segment');
    }
  };

  // Open automation modal
  const openAutomationModal = (automation = null) => {
    if (automation) {
      setEditingAutomation(automation);
      setAutomationForm({
        name: automation.name || '',
        trigger: automation.trigger || 'tag_added',
        tagName: automation.tagName || '',
        action: automation.action || 'send_campaign',
        campaignId: automation.campaignId || '',
        templateId: automation.templateId || '',
        isActive: automation.isActive !== false
      });
    } else {
      setEditingAutomation(null);
      setAutomationForm({
        name: '',
        trigger: 'tag_added',
        tagName: tags.length > 0 ? tags[0].name : '',
        action: 'send_campaign',
        campaignId: '',
        templateId: '',
        isActive: true
      });
    }
    setShowAutomationModal(true);
  };

  // Save automation
  const handleSaveAutomation = async () => {
    if (!automationForm.name || !automationForm.tagName || (!automationForm.campaignId && !automationForm.templateId)) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingAutomation) {
        const response = await axiosInstance.put(`/merchant/automations/${editingAutomation.id}`, automationForm);
        if (response.data.success) {
          toast.success('Automation updated');
        }
      } else {
        const response = await axiosInstance.post('/merchant/automations', automationForm);
        if (response.data.success) {
          toast.success('Automation created');
        }
      }
      setShowAutomationModal(false);
      setEditingAutomation(null);
      fetchAutomations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save automation');
    }
  };

  // Delete automation
  const handleDeleteAutomation = async (id) => {
    if (!window.confirm('Delete this automation?')) return;

    try {
      const response = await axiosInstance.delete(`/merchant/automations/${id}`);
      if (response.data.success) {
        toast.success('Automation deleted');
        fetchAutomations();
      }
    } catch (error) {
      toast.error('Failed to delete automation');
    }
  };

  // Toggle automation active status
  const handleToggleAutomation = async (automation) => {
    try {
      const response = await axiosInstance.put(`/merchant/automations/${automation.id}`, {
        isActive: !automation.isActive
      });
      if (response.data.success) {
        toast.success(automation.isActive ? 'Automation paused' : 'Automation activated');
        fetchAutomations();
      }
    } catch (error) {
      toast.error('Failed to update automation');
    }
  };

  // Auto-tagging rule handlers
  const openAutoTagModal = (rule = null) => {
    if (rule) {
      setEditingAutoTagRule(rule);
      setAutoTagForm({
        name: rule.name || '',
        event: rule.event || 'first_purchase',
        condition: rule.condition || '',
        threshold: rule.threshold || '',
        tags: rule.tags || [],
        isActive: rule.isActive !== false
      });
    } else {
      setEditingAutoTagRule(null);
      setAutoTagForm({
        name: '',
        event: 'first_purchase',
        condition: '',
        threshold: '',
        tags: tags.length > 0 ? [tags[0].name] : [],
        isActive: true
      });
    }
    setShowAutoTagModal(true);
  };

  const handleSaveAutoTagRule = async () => {
    if (!autoTagForm.name || autoTagForm.tags.length === 0) {
      toast.error('Please fill in name and select at least one tag');
      return;
    }

    try {
      if (editingAutoTagRule) {
        const response = await axiosInstance.put(`/merchant/auto-tagging-rules/${editingAutoTagRule.id}`, autoTagForm);
        if (response.data.success) toast.success('Rule updated');
      } else {
        const response = await axiosInstance.post('/merchant/auto-tagging-rules', autoTagForm);
        if (response.data.success) toast.success('Rule created');
      }
      setShowAutoTagModal(false);
      setEditingAutoTagRule(null);
      fetchAutoTaggingRules();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save rule');
    }
  };

  const handleDeleteAutoTagRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      const response = await axiosInstance.delete(`/merchant/auto-tagging-rules/${id}`);
      if (response.data.success) {
        toast.success('Rule deleted');
        fetchAutoTaggingRules();
      }
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const EVENT_LABELS = {
    customer_signup: 'Customer Signs Up',
    first_purchase: 'First Purchase',
    order_completed: 'Any Order Completed',
    order_threshold: 'Order Threshold Reached',
    appointment_booked: 'Appointment Booked'
  };


  // Show loading while checking feature access
  if (featureLoading) {
    return (
      <Shell>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        </div>
      </Shell>
    );
  }

  // Show upgrade prompt if no access
  if (!hasAccess) {
    return (
      <Shell>
        <FeatureGate
          featureId="marketing.tags"
          upgradeMessage="Upgrade to PRO or PREMIUM to access Tags & Segments for customer organization and targeted campaigns"
        >
          <div />
        </FeatureGate>
      </Shell>
    );
  }

  // Show loading while fetching data
  if (loading) {
    return (
      <Shell>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        </div>
      </Shell>
    );
  }

  // Derived: filter + sort the tag list for the management view.
  const visibleTags = (() => {
    const q = tagSearch.trim().toLowerCase();
    const filtered = q
      ? tags.filter(t => t.name.toLowerCase().includes(q))
      : tags.slice();
    if (tagSort === 'mostUsed') {
      filtered.sort((a, b) => (b.count || 0) - (a.count || 0));
    } else if (tagSort === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else {
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return filtered;
  })();

  return (
    <Shell>
        <div className="tags-segments-container tsm-cv2">
          {/* Header — clean text-only title strip */}
          <div className="tsm-header">
            <div className="tsm-header__text">
              <h1 className="tsm-header__title">Tags &amp; Segments</h1>
              <p className="tsm-header__sub">
                Organize customers, create targeted segments, and automate your marketing.
              </p>
            </div>
          </div>

          {/* Pill tabs — Tags / Segments / Automations.
              Tab nav scrolls horizontally on small screens; count chip lives
              on each pill so the data is visible without a full stat card. */}
          <div className="tsm-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'tags'}
              className={`tsm-tab ${activeTab === 'tags' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('tags')}
            >
              <span className="tsm-tab__label">Tags</span>
              <span className="tsm-tab__count">{tags.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'segments'}
              className={`tsm-tab ${activeTab === 'segments' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('segments')}
            >
              <span className="tsm-tab__label">Segments</span>
              <span className="tsm-tab__count">{segments.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'automations'}
              className={`tsm-tab ${activeTab === 'automations' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('automations')}
            >
              <span className="tsm-tab__label">Automations</span>
              <span className="tsm-tab__count">{automations.length + autoTaggingRules.length}</span>
              {(automations.length === 0 && autoTaggingRules.length === 0) && (
                <span className="tsm-tab__badge">New</span>
              )}
            </button>
          </div>

          {/* Sub-header — one-line description for current tab */}
          <div className="tsm-subhead">
            <h2 className="tsm-subhead__title">
              {activeTab === 'tags' && 'Manage tags'}
              {activeTab === 'segments' && 'Manage segments'}
              {activeTab === 'automations' && 'Manage automations'}
            </h2>
            <p className="tsm-subhead__sub">
              {activeTab === 'tags' && 'Create tags to categorize and organize your customers for targeted marketing.'}
              {activeTab === 'segments' && 'Build dynamic customer segments based on tags, purchase history, and behavior.'}
              {activeTab === 'automations' && 'Set up rules to automatically tag customers and trigger email campaigns.'}
            </p>
          </div>

        {/* Tags Tab */}
        {activeTab === 'tags' && (
          <div className={`tsm-tags-grid ${selectedTag ? 'with-panel' : ''}`}>
            {/* Tags list panel */}
            <section className="tsm-card">
              {/* Create tag form — name input + color row + create button */}
              <form onSubmit={handleCreateTag} className="tsm-create-form">
                <input
                  type="text"
                  className="tsm-input"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Enter tag name..."
                />
                <div className="tsm-color-row" role="radiogroup" aria-label="Tag color">
                  {TAG_COLORS.slice(0, 5).map(color => (
                    <button
                      key={color}
                      type="button"
                      role="radio"
                      aria-checked={newTagColor === color}
                      aria-label={`Color ${color}`}
                      onClick={() => setNewTagColor(color)}
                      className={`tsm-color-swatch ${newTagColor === color ? 'is-active' : ''}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  className="tsm-btn tsm-btn--primary"
                  disabled={!newTagName.trim()}
                >
                  Create tag
                </button>
              </form>

              {/* Tags list — sleek row-based list with search + sort.
                  Replaces the chip cloud; scales to many tags and matches the
                  Customers list pattern for visual consistency. */}
              {tags.length === 0 ? (
                <div className="tsm-empty">
                  <h3 className="tsm-empty__title">No tags yet</h3>
                  <p className="tsm-empty__body">
                    Create your first tag above to start segmenting customers.
                  </p>
                </div>
              ) : (
                <>
                  <div className="tsm-list-toolbar">
                    <div className="tsm-search">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        type="text"
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        placeholder="Search tags…"
                      />
                      {tagSearch && (
                        <button
                          type="button"
                          className="tsm-search__clear"
                          aria-label="Clear search"
                          onClick={() => setTagSearch('')}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="tsm-sort" role="radiogroup" aria-label="Sort tags">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={tagSort === 'alpha'}
                        className={`tsm-sort__btn ${tagSort === 'alpha' ? 'is-active' : ''}`}
                        onClick={() => setTagSort('alpha')}
                      >
                        A–Z
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={tagSort === 'mostUsed'}
                        className={`tsm-sort__btn ${tagSort === 'mostUsed' ? 'is-active' : ''}`}
                        onClick={() => setTagSort('mostUsed')}
                      >
                        Most used
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={tagSort === 'newest'}
                        className={`tsm-sort__btn ${tagSort === 'newest' ? 'is-active' : ''}`}
                        onClick={() => setTagSort('newest')}
                      >
                        Newest
                      </button>
                    </div>
                  </div>

                  {visibleTags.length === 0 ? (
                    <div className="tsm-empty">
                      <p className="tsm-empty__body">No tags match “{tagSearch}”.</p>
                    </div>
                  ) : (
                    <ul className="tsm-row-list">
                      {visibleTags.map((tag) => {
                        const isSelected = selectedTag?.name === tag.name;
                        return (
                          <li
                            key={tag.name}
                            className={`tsm-row ${isSelected ? 'is-selected' : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleViewTagCustomers(tag)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleViewTagCustomers(tag);
                              }
                            }}
                          >
                            <span
                              className="tsm-row__dot"
                              style={{ backgroundColor: tag.color || '#6B7280' }}
                              aria-hidden
                            />
                            <span className="tsm-row__name">{tag.name}</span>
                            <span className="tsm-row__count">
                              {tag.count} {(tag.count || 0) === 1 ? 'customer' : 'customers'}
                            </span>
                            <span className="tsm-row__actions">
                              <button
                                type="button"
                                className="tsm-iconbtn tsm-iconbtn--sm"
                                aria-label={`Delete tag ${tag.name}`}
                                onClick={(e) => { e.stopPropagation(); handleDeleteTag(tag.name); }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6M14 11v6" />
                                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                </svg>
                              </button>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </section>

            {/* Tag detail panel — slides in beside the list on desktop,
                appears as a stacked card on mobile. */}
            {selectedTag && (
              <section className="tsm-card tsm-detail">
                <header className="tsm-detail__head">
                  <div className="tsm-detail__title">
                    <span
                      className="tsm-detail__dot"
                      style={{ backgroundColor: selectedTag.color || '#6B7280' }}
                      aria-hidden
                    />
                    <h3 className="tsm-detail__name">{selectedTag.name}</h3>
                    <span className="tsm-detail__count">
                      {tagCustomers.length} {tagCustomers.length === 1 ? 'customer' : 'customers'}
                    </span>
                  </div>
                  <div className="tsm-detail__actions">
                    <button
                      type="button"
                      className="tsm-btn tsm-btn--primary tsm-btn--sm"
                      onClick={openCustomerSelector}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      <span>Add customers</span>
                    </button>
                    <button
                      type="button"
                      className="tsm-iconbtn"
                      onClick={() => setSelectedTag(null)}
                      aria-label="Close"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </header>

                {loadingCustomers ? (
                  <div className="tsm-empty">
                    <p className="tsm-empty__body">Loading…</p>
                  </div>
                ) : tagCustomers.length === 0 ? (
                  <div className="tsm-empty">
                    <h3 className="tsm-empty__title">No customers with this tag</h3>
                    <p className="tsm-empty__body">Add customers to start using this tag in campaigns.</p>
                    <button
                      type="button"
                      className="tsm-btn tsm-btn--primary"
                      onClick={openCustomerSelector}
                    >
                      Add customers
                    </button>
                  </div>
                ) : (
                  <ul className="tsm-detail__list">
                    {tagCustomers.map((customer) => (
                      <li key={customer.id} className="tsm-detail__item">
                        <span className="tsm-detail__avatar" aria-hidden>
                          {(customer.firstName?.[0] || customer.email?.[0] || '?').toUpperCase()}
                        </span>
                        <div className="tsm-detail__person">
                          <span className="tsm-detail__person-name">
                            {customer.firstName} {customer.lastName}
                          </span>
                          {customer.email && (
                            <span className="tsm-detail__person-email">{customer.email}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="tsm-btn tsm-btn--ghost tsm-btn--danger tsm-btn--sm"
                          onClick={() => handleRemoveCustomerFromTag(customer.id)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>
        )}

        {/* Customer Selector Modal */}
        {showCustomerSelector && selectedTag && (
          <div className="tsm-modal-overlay" onClick={() => setShowCustomerSelector(false)}>
            <div
              className="tsm-modal"
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`Add customers to ${selectedTag.name}`}
            >
              <header className="tsm-modal__head">
                <div className="tsm-modal__title-block">
                  <h3 className="tsm-modal__title">
                    Add customers to <em>“{selectedTag.name}”</em>
                  </h3>
                  <p className="tsm-modal__sub">
                    {selectedCustomers.length} selected
                  </p>
                </div>
                <button
                  type="button"
                  className="tsm-iconbtn"
                  onClick={() => setShowCustomerSelector(false)}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </header>

              <div className="tsm-modal__body">
                <div className="tsm-search tsm-search--block">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search customers…"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      fetchAllCustomers(e.target.value);
                    }}
                  />
                </div>

                <div className="tsm-select-toolbar">
                  <button
                    type="button"
                    className="tsm-link"
                    onClick={selectAllCustomers}
                  >
                    Select all
                  </button>
                </div>

                {loadingAllCustomers ? (
                  <div className="tsm-empty"><p className="tsm-empty__body">Loading…</p></div>
                ) : (
                  <ul className="tsm-select-list">
                    {allCustomers
                      .filter(c => !tagCustomers.some(tc => tc.id === c.id))
                      .map(customer => {
                        const checked = selectedCustomers.includes(customer.id);
                        return (
                          <li
                            key={customer.id}
                            className={`tsm-select-item ${checked ? 'is-checked' : ''}`}
                            onClick={() => toggleCustomerSelection(customer.id)}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {}}
                              className="tsm-checkbox"
                            />
                            <span className="tsm-detail__avatar" aria-hidden>
                              {(customer.firstName?.[0] || customer.email?.[0] || '?').toUpperCase()}
                            </span>
                            <div className="tsm-detail__person">
                              <span className="tsm-detail__person-name">
                                {customer.firstName} {customer.lastName}
                              </span>
                              {customer.email && (
                                <span className="tsm-detail__person-email">{customer.email}</span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>

              <footer className="tsm-modal__foot">
                <button
                  type="button"
                  className="tsm-btn tsm-btn--ghost"
                  onClick={() => setShowCustomerSelector(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tsm-btn tsm-btn--primary"
                  onClick={handleAddCustomersToTag}
                  disabled={selectedCustomers.length === 0}
                >
                  Add {selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''}
                </button>
              </footer>
            </div>
          </div>
        )}

        {/* Segments Tab */}
        {activeTab === 'segments' && !showSegmentBuilder && (
          <div className="tsm-pane">
            <div className="tsm-pane__head">
              <p className="tsm-pane__sub">
                Build dynamic segments by combining tags, behavior, and purchase history.
              </p>
              <button
                type="button"
                className="tsm-btn tsm-btn--primary"
                onClick={() => { setEditingSegment(null); setShowSegmentBuilder(true); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>New segment</span>
              </button>
            </div>

            {segments.length === 0 ? (
              <div className="tsm-empty">
                <h3 className="tsm-empty__title">No segments yet</h3>
                <p className="tsm-empty__body">
                  Create your first segment to group customers by their attributes, behavior, and purchase history.
                </p>
                <button
                  type="button"
                  className="tsm-btn tsm-btn--primary"
                  onClick={() => setShowSegmentBuilder(true)}
                >
                  Create your first segment
                </button>
              </div>
            ) : (
              <div className="tsm-segments-grid">
                {segments.map((segment) => (
                  <article key={segment.id} className="tsm-segment-card">
                    <div className="tsm-segment-card__body">
                      <h3 className="tsm-segment-card__name">{segment.name}</h3>
                      {segment.description && (
                        <p className="tsm-segment-card__desc">{segment.description}</p>
                      )}
                      <div className="tsm-segment-card__meta">
                        <span className="tsm-segment-card__metric">
                          {segment.recipientCount || 0}{' '}
                          {(segment.recipientCount || 0) === 1 ? 'customer' : 'customers'}
                        </span>
                        <span className="tsm-segment-card__metric">
                          {segment.rules?.rules?.length || 0}{' '}
                          {(segment.rules?.rules?.length || 0) === 1 ? 'rule' : 'rules'}
                        </span>
                        <span
                          className={`tsm-status ${segment.isActive ? 'tsm-status--ok' : 'tsm-status--off'}`}
                        >
                          <span className="tsm-status__dot" aria-hidden />
                          {segment.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <footer className="tsm-segment-card__foot">
                      <button
                        type="button"
                        className="tsm-btn tsm-btn--ghost tsm-btn--sm"
                        onClick={() => { setEditingSegment(segment); setShowSegmentBuilder(true); }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="tsm-btn tsm-btn--ghost tsm-btn--danger tsm-btn--sm"
                        onClick={() => handleDeleteSegment(segment.id)}
                      >
                        Delete
                      </button>
                    </footer>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Segment Builder */}
        {activeTab === 'segments' && showSegmentBuilder && (
          <VisualSegmentBuilder
            segment={editingSegment}
            existingTags={tags}
            onSave={handleSaveSegment}
            onCancel={() => { setShowSegmentBuilder(false); setEditingSegment(null); }}
          />
        )}

        {/* Automations Tab */}
        {activeTab === 'automations' && (
          <div className="tsm-pane">
            {/* Tag-driven email automations */}
            <div className="tsm-pane__head">
              <p className="tsm-pane__sub">
                Automatically send a campaign when a customer is tagged.
              </p>
              <button
                type="button"
                className="tsm-btn tsm-btn--primary"
                onClick={() => openAutomationModal()}
                disabled={tags.length === 0 || (campaigns.length === 0 && templates.length === 0)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>New automation</span>
              </button>
            </div>

            {tags.length === 0 && (
              <div className="tsm-banner tsm-banner--warn">
                Create some tags first before setting up automations.
              </div>
            )}

            {campaigns.length === 0 && templates.length === 0 && tags.length > 0 && (
              <div className="tsm-banner tsm-banner--warn">
                Create an email template or campaign first to use with automations.
              </div>
            )}

            {automations.length === 0 ? (
              <div className="tsm-empty">
                <h3 className="tsm-empty__title">No automations yet</h3>
                <p className="tsm-empty__body">
                  Set up automations to send campaigns when customers receive specific tags.
                </p>
              </div>
            ) : (
              <ul className="tsm-row-list">
                {automations.map((automation) => (
                  <li key={automation.id} className="tsm-rule-row">
                    <div className="tsm-rule-row__main">
                      <div className="tsm-rule-row__title">
                        <span className="tsm-rule-row__name">{automation.name}</span>
                        <span
                          className={`tsm-status ${automation.isActive ? 'tsm-status--ok' : 'tsm-status--off'}`}
                        >
                          <span className="tsm-status__dot" aria-hidden />
                          {automation.isActive ? 'Active' : 'Paused'}
                        </span>
                      </div>
                      <div className="tsm-rule-row__desc">
                        <span>When tag</span>
                        <span className="tsm-token">{automation.tagName}</span>
                        <span className="tsm-arrow" aria-hidden>→</span>
                        <span>Send <em>“{automation.campaignName}”</em></span>
                      </div>
                    </div>
                    <div className="tsm-rule-row__actions">
                      <button
                        type="button"
                        className={`tsm-btn tsm-btn--ghost tsm-btn--sm ${automation.isActive ? 'tsm-btn--danger' : ''}`}
                        onClick={() => handleToggleAutomation(automation)}
                      >
                        {automation.isActive ? 'Pause' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        className="tsm-btn tsm-btn--ghost tsm-btn--sm"
                        onClick={() => openAutomationModal(automation)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="tsm-btn tsm-btn--ghost tsm-btn--danger tsm-btn--sm"
                        onClick={() => handleDeleteAutomation(automation.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Auto-Tagging Rules subsection */}
            <div className="tsm-subsection">
              <div className="tsm-pane__head">
                <div className="tsm-pane__subhead">
                  <h3 className="tsm-pane__subhead-title">Auto-tagging rules</h3>
                  <p className="tsm-pane__sub">
                    Automatically tag customers based on their behavior.
                  </p>
                </div>
                <button
                  type="button"
                  className="tsm-btn tsm-btn--primary"
                  onClick={() => openAutoTagModal()}
                  disabled={tags.length === 0}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span>Add rule</span>
                </button>
              </div>

              {autoTaggingRules.length === 0 ? (
                <div className="tsm-empty">
                  <p className="tsm-empty__body">
                    No auto-tagging rules yet. Create rules to automatically tag customers when they sign up, make purchases, or hit spending thresholds.
                  </p>
                </div>
              ) : (
                <ul className="tsm-row-list">
                  {autoTaggingRules.map((rule) => (
                    <li key={rule.id} className="tsm-rule-row">
                      <div className="tsm-rule-row__main">
                        <div className="tsm-rule-row__title">
                          <span className="tsm-rule-row__name">{rule.name}</span>
                          <span
                            className={`tsm-status ${rule.isActive ? 'tsm-status--ok' : 'tsm-status--off'}`}
                          >
                            <span className="tsm-status__dot" aria-hidden />
                            {rule.isActive ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        <div className="tsm-rule-row__desc">
                          <span>When</span>
                          <span className="tsm-token">{EVENT_LABELS[rule.event] || rule.event}</span>
                          {rule.event === 'order_threshold' && rule.condition && (
                            <span className="tsm-rule-row__qualifier">
                              ({rule.condition === 'order_count' ? 'orders' : 'total spent'} ≥{' '}
                              {rule.condition === 'total_spent' ? '₦' : ''}{rule.threshold})
                            </span>
                          )}
                          <span className="tsm-arrow" aria-hidden>→</span>
                          <span>Add tags:</span>
                          {(rule.tags || []).map((tag) => (
                            <span key={tag} className="tsm-token">{tag}</span>
                          ))}
                        </div>
                      </div>
                      <div className="tsm-rule-row__actions">
                        <button
                          type="button"
                          className="tsm-btn tsm-btn--ghost tsm-btn--sm"
                          onClick={() => openAutoTagModal(rule)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="tsm-btn tsm-btn--ghost tsm-btn--danger tsm-btn--sm"
                          onClick={() => handleDeleteAutoTagRule(rule.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Auto-Tagging Rule Modal */}
        {showAutoTagModal && (
          <div className="tsm-modal-overlay" onClick={() => setShowAutoTagModal(false)}>
            <div
              className="tsm-modal"
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="autotag-modal-title"
            >
              <header className="tsm-modal__head">
                <div className="tsm-modal__title-block">
                  <h3 id="autotag-modal-title" className="tsm-modal__title">
                    {editingAutoTagRule ? 'Edit auto-tagging rule' : 'Create auto-tagging rule'}
                  </h3>
                  <p className="tsm-modal__sub">
                    Run this rule whenever a matching event happens.
                  </p>
                </div>
                <button
                  type="button"
                  className="tsm-iconbtn"
                  onClick={() => setShowAutoTagModal(false)}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </header>

              <div className="tsm-modal__body">
                <div className="tsm-field">
                  <label className="tsm-field__label" htmlFor="autotag-name">Rule name *</label>
                  <input
                    id="autotag-name"
                    type="text"
                    className="tsm-input"
                    value={autoTagForm.name}
                    onChange={(e) => setAutoTagForm({ ...autoTagForm, name: e.target.value })}
                    placeholder="e.g., Tag VIP buyers"
                  />
                </div>

                <div className="tsm-field">
                  <label className="tsm-field__label" htmlFor="autotag-event">When this happens *</label>
                  <select
                    id="autotag-event"
                    className="tsm-input"
                    value={autoTagForm.event}
                    onChange={(e) => setAutoTagForm({ ...autoTagForm, event: e.target.value, condition: '', threshold: '' })}
                  >
                    <option value="customer_signup">Customer signs up</option>
                    <option value="first_purchase">Customer makes first purchase</option>
                    <option value="order_completed">Any order completed</option>
                    <option value="order_threshold">Order threshold reached</option>
                    <option value="appointment_booked">Appointment booked</option>
                  </select>
                </div>

                {autoTagForm.event === 'order_threshold' && (
                  <>
                    <div className="tsm-field">
                      <label className="tsm-field__label" htmlFor="autotag-threshold-type">Threshold type</label>
                      <select
                        id="autotag-threshold-type"
                        className="tsm-input"
                        value={autoTagForm.condition}
                        onChange={(e) => setAutoTagForm({ ...autoTagForm, condition: e.target.value })}
                      >
                        <option value="">Select threshold type…</option>
                        <option value="order_count">Number of orders</option>
                        <option value="total_spent">Total amount spent (₦)</option>
                      </select>
                    </div>
                    <div className="tsm-field">
                      <label className="tsm-field__label" htmlFor="autotag-threshold-value">Threshold value</label>
                      <input
                        id="autotag-threshold-value"
                        type="number"
                        className="tsm-input"
                        value={autoTagForm.threshold}
                        onChange={(e) => setAutoTagForm({ ...autoTagForm, threshold: e.target.value })}
                        placeholder={autoTagForm.condition === 'total_spent' ? 'e.g., 50000' : 'e.g., 5'}
                      />
                      <p className="tsm-field__hint">
                        {autoTagForm.condition === 'total_spent'
                          ? 'Customer will be tagged when their total spend reaches this amount.'
                          : 'Customer will be tagged when they complete this many orders.'}
                      </p>
                    </div>
                  </>
                )}

                <div className="tsm-field">
                  <label className="tsm-field__label">Tags to add *</label>
                  <div className="tsm-chip-wrap">
                    {(autoTagForm.tags || []).map(tag => (
                      <span key={tag} className="tsm-chip">
                        {tag}
                        <button
                          type="button"
                          className="tsm-chip__remove"
                          aria-label={`Remove ${tag}`}
                          onClick={() => setAutoTagForm({
                            ...autoTagForm,
                            tags: autoTagForm.tags.filter(t => t !== tag)
                          })}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                  <select
                    className="tsm-input"
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !autoTagForm.tags.includes(e.target.value)) {
                        setAutoTagForm({
                          ...autoTagForm,
                          tags: [...autoTagForm.tags, e.target.value]
                        });
                      }
                    }}
                  >
                    <option value="">Select a tag to add…</option>
                    {tags.filter(t => !(autoTagForm.tags || []).includes(t.name)).map(tag => (
                      <option key={tag.name} value={tag.name}>{tag.name}</option>
                    ))}
                  </select>
                </div>

                <label className="tsm-toggle">
                  <input
                    type="checkbox"
                    checked={autoTagForm.isActive}
                    onChange={(e) => setAutoTagForm({ ...autoTagForm, isActive: e.target.checked })}
                  />
                  <span>Rule is active</span>
                </label>
              </div>

              <footer className="tsm-modal__foot">
                <button
                  type="button"
                  className="tsm-btn tsm-btn--ghost"
                  onClick={() => setShowAutoTagModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tsm-btn tsm-btn--primary"
                  onClick={handleSaveAutoTagRule}
                  disabled={!autoTagForm.name || (autoTagForm.tags || []).length === 0}
                >
                  {editingAutoTagRule ? 'Update rule' : 'Create rule'}
                </button>
              </footer>
            </div>
          </div>
        )}

        {/* Automation Modal */}
        {showAutomationModal && (
          <div className="tsm-modal-overlay" onClick={() => setShowAutomationModal(false)}>
            <div
              className="tsm-modal"
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="automation-modal-title"
            >
              <header className="tsm-modal__head">
                <div className="tsm-modal__title-block">
                  <h3 id="automation-modal-title" className="tsm-modal__title">
                    {editingAutomation ? 'Edit automation' : 'Create automation'}
                  </h3>
                  <p className="tsm-modal__sub">
                    Send a campaign automatically when a tag is applied to a customer.
                  </p>
                </div>
                <button
                  type="button"
                  className="tsm-iconbtn"
                  onClick={() => setShowAutomationModal(false)}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </header>

              <div className="tsm-modal__body">
                <div className="tsm-field">
                  <label className="tsm-field__label" htmlFor="automation-name">Automation name *</label>
                  <input
                    id="automation-name"
                    type="text"
                    className="tsm-input"
                    value={automationForm.name}
                    onChange={(e) => setAutomationForm({ ...automationForm, name: e.target.value })}
                    placeholder="e.g., Welcome VIP customers"
                  />
                </div>

                <div className="tsm-field">
                  <label className="tsm-field__label" htmlFor="automation-tag">When customer receives tag *</label>
                  <select
                    id="automation-tag"
                    className="tsm-input"
                    value={automationForm.tagName}
                    onChange={(e) => setAutomationForm({ ...automationForm, tagName: e.target.value })}
                  >
                    <option value="">Select a tag…</option>
                    {tags.map(tag => (
                      <option key={tag.name} value={tag.name}>{tag.name}</option>
                    ))}
                  </select>
                </div>

                <div className="tsm-field">
                  <label className="tsm-field__label" htmlFor="automation-target">Send email using *</label>
                  <select
                    id="automation-target"
                    className="tsm-input"
                    value={automationForm.templateId ? `template:${automationForm.templateId}` : automationForm.campaignId ? `campaign:${automationForm.campaignId}` : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.startsWith('template:')) {
                        setAutomationForm({
                          ...automationForm,
                          templateId: value.replace('template:', ''),
                          campaignId: ''
                        });
                      } else if (value.startsWith('campaign:')) {
                        setAutomationForm({
                          ...automationForm,
                          campaignId: value.replace('campaign:', ''),
                          templateId: ''
                        });
                      } else {
                        setAutomationForm({
                          ...automationForm,
                          campaignId: '',
                          templateId: ''
                        });
                      }
                    }}
                  >
                    <option value="">Select a template or campaign…</option>
                    {templates.length > 0 && (
                      <optgroup label="Email Templates">
                        {templates.map(template => (
                          <option key={`template-${template.id}`} value={`template:${template.id}`}>
                            {template.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {campaigns.length > 0 && (
                      <optgroup label="Campaigns">
                        {campaigns.map(campaign => (
                          <option key={`campaign-${campaign.id}`} value={`campaign:${campaign.id}`}>
                            {campaign.name} ({campaign.type})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <p className="tsm-field__hint">
                    Pick a template or existing campaign to send when customers receive this tag.
                  </p>
                </div>

                <label className="tsm-toggle">
                  <input
                    type="checkbox"
                    checked={automationForm.isActive}
                    onChange={(e) => setAutomationForm({ ...automationForm, isActive: e.target.checked })}
                  />
                  <span>Automation is active</span>
                </label>
              </div>

              <footer className="tsm-modal__foot">
                <button
                  type="button"
                  className="tsm-btn tsm-btn--ghost"
                  onClick={() => setShowAutomationModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tsm-btn tsm-btn--primary"
                  onClick={handleSaveAutomation}
                  disabled={!automationForm.name || !automationForm.tagName || (!automationForm.campaignId && !automationForm.templateId)}
                >
                  {editingAutomation ? 'Update automation' : 'Create automation'}
                </button>
              </footer>
            </div>
          </div>
        )}
        </div>
    </Shell>
  );
};

export default TagsSegmentsManagement;
